import React from "react";
import { Card, Tag, Space, Progress, Typography } from "antd";
import { Link } from "react-router-dom";
import OrgLinks from "../shared/OrgLinks";
import { decodeStr } from "../../utils";
import OrgChurnStatus from "../shared/OrgChurnStatus";

const { Text } = Typography;

const OrgCard = ({ org, billingUsageMap, churnIndicator, churnLoading }) => {
  const istanzaColors = {
    free: "blue",
    demo: "orange",
    production: "green",
  };

  const ista = (org?.tipo_istanza || "").toLowerCase();
  const isProduction = ista === "production";
  const statoColor = org.stato === "attiva" ? "green" : "red";
  const s = billingUsageMap?.[org.objectId];

  // Helper function to convert values to numbers
  const toNum = (v) =>
    typeof v === "number"
      ? v
      : v != null
      ? parseFloat(String(v).replace(",", "."))
      : null;

  // Calculate usage values (same logic as table columns)
  const renderServizi = () => {
    if (!isProduction || !s) return null;

    const flat = !!s.limit_is_flat;
    const used = toNum(s.total_count);
    const limit = toNum(s.purchased_amount);

    if (flat) {
      return (
        <div className="org-card-usage-row">
          <Text type="secondary">Servizi:</Text>
          <span>{Number.isFinite(used) ? used : "-"} / FLAT</span>
        </div>
      );
    }

    const pctRaw =
      typeof s.pct_used === "number"
        ? s.pct_used
        : Number.isFinite(used) && Number.isFinite(limit) && limit > 0
        ? (used / limit) * 100
        : null;
    const pct = Number.isFinite(pctRaw) ? Math.round(pctRaw) : null;
    const pctClamped = Number.isFinite(pctRaw)
      ? Math.max(0, Math.min(100, Math.round(pctRaw)))
      : 0;

    const strokeColor = (() => {
      if (!Number.isFinite(pctRaw)) return undefined;
      if (
        pctRaw > 100 ||
        (Number.isFinite(used) && Number.isFinite(limit) && used > limit)
      )
        return "#52c41a";
      if (pctRaw < 50) return "#f5222d";
      if (pctRaw < 80) return "#fa8c16";
      return "#3D5864";
    })();

    return (
      <div className="org-card-usage-row">
        <Text type="secondary">Servizi:</Text>
        <div className="org-card-progress-wrapper">
          <Progress
            percent={pctClamped}
            size="small"
            showInfo
            strokeColor={strokeColor}
            format={() => (pct != null ? `${pct}%` : "")}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {Number.isFinite(used) ? used : "-"} /{" "}
            {Number.isFinite(limit) ? limit : "-"}
          </Text>
        </div>
      </div>
    );
  };

  const renderProiezione = () => {
    if (!isProduction || !s) return null;
    if (s.needs_more_data) return null;
    if (!s.prediction_available) {
      return (
        <div className="org-card-usage-row">
          <Text type="secondary">Proiezione:</Text>
          <Tag>n/d</Tag>
        </div>
      );
    }

    const pctRaw = toNum(s.pct_projected);
    const pct = Number.isFinite(pctRaw) ? Math.round(pctRaw) : null;
    const pctClamped = Number.isFinite(pctRaw)
      ? Math.max(0, Math.min(100, Math.round(pctRaw)))
      : 0;

    const strokeColor = (() => {
      if (!Number.isFinite(pctRaw)) return undefined;
      if (
        pctRaw > 100 ||
        (typeof s.pct_used === "number" && s.pct_used > 100)
      )
        return "#52c41a";
      if (pctRaw < 50) return "#f5222d";
      if (pctRaw < 80) return "#fa8c16";
      return "#3D5864";
    })();

    const exp = toNum(s.expected_total);

    return (
      <div className="org-card-usage-row">
        <Text type="secondary">Proiezione:</Text>
        <div className="org-card-progress-wrapper">
          <Progress
            percent={pctClamped}
            size="small"
            showInfo
            strokeColor={strokeColor}
            format={() => (pct != null ? `${pct}%` : "")}
          />
          {Number.isFinite(exp) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {Math.round(exp)}
            </Text>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="org-card" size="small">
      {/* Header with ID and Status Tags */}
      <div className="org-card-header">
        <Link to={`/orgs/${org.objectId}`} className="org-card-id">
          {org.objectId}
        </Link>
        <Space size={4}>
          {org.tipo_istanza && (
            <Tag color={istanzaColors[org.tipo_istanza]}>
              {org.tipo_istanza.toUpperCase()}
            </Tag>
          )}
          {org.stato && (
            <Tag color={statoColor}>{org.stato.toUpperCase()}</Tag>
          )}
        </Space>
      </div>

      <div style={{ marginBottom: 8 }}>
        <OrgChurnStatus
          indicator={churnIndicator}
          compact
          loading={churnLoading}
          hideNotApplicable
        />
      </div>

      {/* Company Name - Primary emphasis */}
      <div className="org-card-title">
        <Link to={`/orgs/${org.objectId}`}>{decodeStr(org.nome)}</Link>
      </div>

      {/* Usage Section (only for production) */}
      {isProduction && s && (
        <div className="org-card-usage">
          {renderServizi()}
          {renderProiezione()}
        </div>
      )}

      {/* Links Section */}
      <div className="org-card-links">
        <OrgLinks org={org} />
      </div>
    </Card>
  );
};

export default OrgCard;
